import store from "../redux/Store";
import { userActions } from "../redux/reducers/UserReducer";
import { commonActions } from "../redux/reducers/CommonReducer";
import * as actionsService from "./ActionsService/ActionsService";
import { getCurrentSites } from "../utils/UsersUtils";

const getScopesIds = (state, providedScopesIds, providedCurrentUsersScopes) => {
  if (providedScopesIds && providedCurrentUsersScopes) {
    return {
      scopesIds: providedScopesIds,
      currentUsersScopesForSites: providedCurrentUsersScopes
    };
  } else {
    const currentUsersScopesForSites = state.user.currentUsersScopes;
    return {
      scopesIds: currentUsersScopesForSites.map(userScope => userScope.id),
      currentUsersScopesForSites: currentUsersScopesForSites
    };
  }
};

// Unified refresh logic - handles both single user and all users
// userIds: null/empty = collect all user IDs from sites (full refresh), array = use provided IDs (single/multiple users)
export const refreshUsersData = (userIds = null, providedScopesIds = null, providedCurrentUsersScopes = null, 
                  isShowSpinner = null, closeSpinner = null, setAllParkers = false) => {
  const state = store.getState();
  const selectedScope = state.user?.selectedScope;

  // Check if selectedScope is set before proceeding
  if (!selectedScope) {
    console.warn('Cannot refresh users data: selectedScope not set');
    return Promise.resolve();
  }

  const currentInProcess = state.common.isUsersDataFetching;
  if (currentInProcess && !providedScopesIds) {
    return Promise.resolve();
  }

  const { scopesIds, currentUsersScopesForSites } = getScopesIds(state, providedScopesIds, providedCurrentUsersScopes);

  const currentSites = state.user.sites;
  const currentIsFirstTimeLoading = state.common.isFirstTimeLoading;
  const currentSelectedScope = state.user.selectedScope;

  let ids = [];
  if (userIds && userIds.length > 0) {
    ids = Array.isArray(userIds) ? userIds : [userIds];
  } else {
    let filteredSites = getCurrentSites(currentSites, currentUsersScopesForSites);
    filteredSites.forEach((site) => {
      site?.userSites.forEach((userSite) => {
        if (userSite.userId && !ids.some((id) => id === userSite.userId)) {
          ids.push(userSite.userId);
        }
      });
      site?.tokens.forEach((token) => {
        token.carUserTokens?.forEach((carUserToken) => {
          if (carUserToken.userId && !ids.some((id) => id === carUserToken.userId)) {
            ids.push(carUserToken.userId);
          }
        });
      });
    });
  }

  if (ids.length > 0) {
    // If scope change is happening (providedScopesIds), prioritize it even if refresh is in process
    if (!currentInProcess || providedScopesIds) {
      store.dispatch(commonActions.setIsUsersDataFetching(true));
      
      const isFullRefresh = !userIds || userIds.length === 0;      
      const shouldShowSpinner = isShowSpinner !== null ? isShowSpinner : currentIsFirstTimeLoading;      
      const shouldCloseSpinner = closeSpinner !== null ? closeSpinner : !currentIsFirstTimeLoading;
      
      return actionsService.getUsersMinimalIdentityData(ids, scopesIds, selectedScope, shouldShowSpinner, shouldCloseSpinner).then((response) => {
        if (response.data?.entity) {
          store.dispatch(userActions.addMultipleUsersDataToParker(response.data.entity));
          
          if (isFullRefresh) {
            store.dispatch(userActions.setParkersAndGuests({setAllParkers: setAllParkers}));
          }
        }

        // Only set isFirstTimeLoading to false for full refresh (all users)
        if (isFullRefresh) {
          store.dispatch(commonActions.setIsFirstTimeLoading(false));
        }

        store.dispatch(commonActions.setIsUsersDataFetching(false));       
        if (currentSelectedScope && isFullRefresh && closeSpinner !== false) {
          store.dispatch(commonActions.setIsShowLoadingSpinner(false));
        }
        return response;
      }).catch((error) => {
        store.dispatch(commonActions.setIsUsersDataFetching(false));
        console.error('Error refreshing users data:', error);
        throw error;
      });
    }
    return Promise.resolve();
  } else {
    store.dispatch(userActions.resetTablesData());
    if (currentSites.length > 0 && currentSelectedScope !== null) {
      store.dispatch(commonActions.setIsShowLoadingSpinner(false));
    }
    return Promise.resolve();
  }
};


export const refreshAllUsersData = (providedScopesIds = null, providedCurrentUsersScopes = null, isShowSpinner = null, closeSpinner = null, setAllParkers = false) => {
  return refreshUsersData(null, providedScopesIds, providedCurrentUsersScopes, isShowSpinner, closeSpinner, setAllParkers);
};

export const refreshSingleUserData = (userId, providedScopesIds = null, providedCurrentUsersScopes = null, isShowSpinner = null, closeSpinner = null) => {
  if (!userId) {
    return Promise.reject(new Error('Cannot refresh user data: userId not provided'));
  }
  return refreshUsersData([userId], providedScopesIds, providedCurrentUsersScopes, isShowSpinner, closeSpinner)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      console.error("!!!!! refreshSingleUserData error", error);
      throw error;
    });
};

export const refreshSelectedUserExtendedData = (userId, providedScopesIds = null, providedCurrentUsersScopes = null, isShowSpinner = null, closeSpinner = null) => {
  if (!userId) {
    return Promise.reject(new Error('Cannot refresh selected user extended data: userId not provided'));
  }

  const state = store.getState();
  const { scopesIds } = getScopesIds(state, providedScopesIds, providedCurrentUsersScopes);

  return actionsService.getSelectedUserData(userId, scopesIds, isShowSpinner ?? false, closeSpinner ?? true).then((response) => {
    if (response.data?.entity) {
      const entity = response.data.entity;
      const extendedData = {
        parkingHistories: entity.parkingHistories || entity.utronUser?.parkingHistories,
        invoiceSummaries: entity.invoiceSummaries || entity.utronUser?.invoiceSummaries,        
      };

      // Update extended data in currentUsersSite
      store.dispatch(userActions.updateUserExtendedData({
        userId: userId,
        extendedData: extendedData
      }));

      // Process parker with updated extended data to recalculate derived fields
      store.dispatch(userActions.processSingleParkerInState({ userId }));
      return response;
    } else {
      return response;
    }
  }).catch((error) => {
    console.error('Error refreshing selected user extended data:', error);    
    throw error;
  });
};

// Common method to refresh both single user data and extended data sequentially
export const refreshSingleUserDataAndExtended = (userId, providedScopesIds = null, providedCurrentUsersScopes = null, isShowSpinner = null, closeSpinner = null) => {
  if (!userId) {
    return Promise.reject(new Error('Cannot refresh user data: userId not provided'));
  }

  return refreshSingleUserData(userId, providedScopesIds, providedCurrentUsersScopes, isShowSpinner, closeSpinner)
    .then(() => {
      // Process parker with fresh basic user data from API response
      store.dispatch(userActions.processSingleParkerInState({ userId }));
      // refreshSelectedUserExtendedData will call processSingleParkerInState internally after updating extended data
      return refreshSelectedUserExtendedData(userId, providedScopesIds, providedCurrentUsersScopes, isShowSpinner, closeSpinner);
    })
    .then((extendedResponse) => {      
      const state = store.getState();
      const fullUser = state.user.currentUsersSite.find(
        (parker) => parker?.identityId === userId || parker?.utronUser?.id === userId
      );
      
      if (fullUser) {        
        const fullUserCopy = {
          ...fullUser,
          utronUser: fullUser.utronUser ? { ...fullUser.utronUser } : null,
          userToPlatforms: fullUser.userToPlatforms ? [...fullUser.userToPlatforms] : null
        };
        return {
          ...extendedResponse,
          data: {
            ...extendedResponse.data,
            entity: [fullUserCopy]
          }
        };
      }
      // Fallback to extended response if user not found in state
      return extendedResponse;
    })
    .catch((error) => {
      throw error;
    });
};

/**
 * Handles updating GMT user after a user update operation.
 * If the updated user is the logged-in user, refetches complete user data (including userScopes).
 * Otherwise, updates with the response data from the update operation.
 * @param {string} updatedUserId - The identity ID of the user that was updated
 * @param {object} updateResponse - The response from the updateUser API call
 * @param {boolean} isShowSpinner - Whether to show spinner when refetching (default: true)
 * @param {boolean} closeSpinner - Whether to close spinner after refetching (default: false)
 */
export const handleGmtUserUpdateAfterUserEdit = (updatedUserId, updateResponse, isShowSpinner = true, closeSpinner = false) => {
  const state = store.getState();
  const gmtUser = state.user.gmtUser;
  
  // Check if the updated user is the logged-in user
  const isUpdatingLoggedInUser = gmtUser?.userIdentity?.id === updatedUserId;
  
  if (isUpdatingLoggedInUser) {
    // Refetch complete logged-in user data to get userScopes and all latest data
    return actionsService.getLoggedInUser(updatedUserId, isShowSpinner, closeSpinner)
      .then((loggedInUserResponse) => {
        if (loggedInUserResponse.data.isSuccess) {
          store.dispatch(userActions.updateGmtUser(loggedInUserResponse.data));
        }
        return loggedInUserResponse;
      })
      .catch((error) => {
        console.log("Error fetching logged-in user data:", error);
        throw error;
      });
  } else {
    // For other users, just update with the response data
    store.dispatch(userActions.updateGmtUser(updateResponse));
    return Promise.resolve(updateResponse);
  }
};

